import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Lightning, MapPin, Package } from "@phosphor-icons/react";
import SectionEyebrow from "@/components/shared/SectionEyebrow";
import LiveDeliveryMap from "@/components/home/LiveDeliveryMap";

export default function Hero() {
  return (
    <section className="relative overflow-hidden ih-noise" data-testid="hero-section">
      {/* Background grid + radial */}
      <div className="absolute inset-0 ih-grid-bg [mask-image:radial-gradient(60%_60%_at_50%_30%,#000_60%,transparent_100%)]" />
      <div className="absolute -top-40 -left-40 w-[700px] h-[700px] ih-radial-blue" />
      <div className="absolute -bottom-40 -right-40 w-[700px] h-[700px] ih-radial-neon" />

      <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 pt-20 lg:pt-28 pb-24">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-6 items-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="lg:col-span-7 space-y-8"
          >
            <SectionEyebrow label="Caribbean Logistics · Trinidad & Tobago" />

            <h1 className="font-display font-black text-white text-balance leading-[0.95] text-5xl sm:text-6xl lg:text-[5.5rem]">
              Deliver Anything.
              <br />
              <span className="text-white/70">Anytime.</span>{" "}
              <span className="relative inline-block">
                <span className="relative z-10">Anywhere.</span>
                <span className="absolute -bottom-1 left-0 right-0 h-2 bg-ih-neon/80 -z-0 -skew-x-12" />
              </span>
            </h1>

            <p className="text-white/70 text-lg lg:text-xl max-w-xl text-pretty leading-relaxed">
              IslandHop connects customers, businesses, and independent drivers through a fast,
              reliable delivery platform built for Trinidad &amp; Tobago and the wider Caribbean.
            </p>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <a
                href="#download"
                data-testid="hero-cta-download"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-ih-electric text-white font-semibold hover:scale-[1.02] transition ih-shadow-blue"
              >
                Download App <ArrowUpRight weight="bold" size={16} />
              </a>
              <Link
                to="/drivers"
                data-testid="hero-cta-driver"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-ih-neon text-black font-semibold hover:scale-[1.02] transition ih-shadow-neon"
              >
                Become a Driver
              </Link>
              <Link
                to="/partners"
                data-testid="hero-cta-partner"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-ih-line2 text-white font-semibold hover:bg-white/[0.04] hover:border-white/30 transition"
              >
                Partner Your Business
              </Link>
            </div>

            {/* Trust strip */}
            <div className="grid grid-cols-3 gap-6 pt-10 max-w-xl border-t border-ih-line pt-8">
              <Stat value="< 35min" label="Avg. delivery target" />
              <Stat value="5" label="Service categories" />
              <Stat value="24/7" label="Operations" />
            </div>
          </motion.div>

          {/* Right: device + map visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.1, ease: "easeOut" }}
            className="lg:col-span-5 relative"
            data-testid="hero-visual"
          >
            <div className="relative ih-glass rounded-3xl p-3 ih-shadow-blue overflow-hidden">
              <LiveDeliveryMap />
              {/* Floating cards */}
              <FloatingCard className="absolute -left-6 top-12 ih-shadow-neon" tone="neon" icon={<Lightning weight="fill" />} title="Driver accepted" sub="ETA 12 min · Port of Spain" />
              <FloatingCard className="absolute -right-4 bottom-16 ih-shadow-orange" tone="orange" icon={<Package weight="fill" />} title="Order #IH-4821" sub="Grocery · 7 items" />
              <FloatingCard className="absolute left-6 -bottom-4" tone="electric" icon={<MapPin weight="fill" />} title="Tracking live" sub="GPS · 3.2 km away" />
            </div>
          </motion.div>
        </div>

        {/* Ticker */}
        <div className="mt-24 border-y border-ih-line py-4 overflow-hidden">
          <div className="flex gap-12 whitespace-nowrap animate-ticker">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="flex gap-12 shrink-0 text-white/40 font-mono text-xs uppercase tracking-[0.22em]">
                <span>Delivery</span><span className="text-ih-teal">●</span>
                <span>Handyman</span><span className="text-ih-teal">●</span>
                <span>Cleaning</span><span className="text-ih-teal">●</span>
                <span>Tech Support</span><span className="text-ih-teal">●</span>
                <span>Events</span><span className="text-ih-teal">●</span>
                <span>Trinidad &amp; Tobago</span><span className="text-ih-teal">●</span>
                <span>Wider Caribbean</span><span className="text-ih-teal">●</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const Stat = ({ value, label }) => (
  <div>
    <p className="font-display font-extrabold text-3xl text-white">{value}</p>
    <p className="text-xs text-white/50 mt-1">{label}</p>
  </div>
);

const FloatingCard = ({ className = "", tone = "electric", icon, title, sub }) => {
  const ring = {
    neon: "ring-ih-neon/40 text-ih-neon",
    electric: "ring-ih-electric/50 text-ih-electric",
    orange: "ring-ih-orange/50 text-ih-orange",
  }[tone];
  return (
    <div className={`ih-glass rounded-2xl px-4 py-3 flex items-center gap-3 shadow-2xl ring-1 ${ring} ${className}`}>
      <span className={`w-9 h-9 rounded-xl bg-black/40 grid place-items-center text-current`}>{icon}</span>
      <div>
        <p className="text-sm font-semibold text-white leading-tight">{title}</p>
        <p className="text-xs text-white/60 leading-tight">{sub}</p>
      </div>
    </div>
  );
};
